import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import 'dayjs/locale/fr';
import 'dayjs/locale/es';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/en';
import 'dayjs/locale/hu';
import { locale } from 'dayjs';
import { combineLatest, fromEvent, of } from 'rxjs';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Paths } from '@config/paths.config';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { CartStoreRefiner } from '@common/refiners/cart.refiner';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { PaymentStoreSelector } from './common/selectors/payment.selector';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { OnSiteLocationStoreSelector } from '@common/selectors/on-site-location.selector';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { BasketStoreDispatcher } from '@common/dispatchers/basket.dispatcher';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';
import { SettingsStoreDispatcher } from '@common/dispatchers/settings.dispatcher';
import { OnSiteLocationStoreDispatcher } from '@common/dispatchers/on-site-location.dispatcher';

import { DoodOrderStatus } from '@store/order/order.model';
import { DoodLocationParameters } from '@store/settings/settings.model';
import { DoodOnSiteLocationModel } from '@core/models/on-site-location.model';

import { CartKeys } from '@config/keys/cart.keys';
import { UrlParamsKeys } from '@config/keys/url-params.keys';
import { OnSiteLocationKeys } from '@config/keys/on-site-location.keys';
import { DistributionModeKeys, MarketplaceKeys, ShopKeys } from '@config/keys/shop.keys';

import {
  DistributionModeValues,
  OnSiteLocationPickModeValues,
  OrderTypeValues,
} from '@config/values/order.values';
import { MarketplaceCartScopeValues } from '@config/values/marketplace.values';
import { isMarketplaceDomainOverrideHost } from '@app/utilities/domain.util';
import {
  buildMarketplaceLocaleContext,
  decideMarketplaceLocale,
  marketplaceLocaleRedirectUrl,
} from '@core/services/i18n/marketplace-locale-url';

import { BasketStoreRefiner } from './common/refiners/basket.refiner';
import { DoodOnSiteLocationSourceValues } from '@store/on-site-location/on-site-location.enum';

import { PwaService } from '@core/services/pwa/pwa.service';
import { ComoService } from '@core/services/como/como.service';
import { CartService } from '@core/services/cart/cart.service';
import { KioskService } from '@core/services/kiosk/kiosk.service';
import { OrdersService } from '@core/services/orders/orders.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { UpdateService } from '@core/services/update/update.service';
import { BasketService } from '@core/services/basket/basket.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { CanonicalService } from '@core/services/canonical/canonical.service';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';
import { DoodAppLoaderService } from '@core/services/dood-app-loader/dood-app-loader.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { NoConnectionModalService } from '@core/services/no-connection-modal/no-connection-modal.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { HiddenCommentStorageService } from '@core/services/hidden-comment-storage/hidden-comment-storage.service';
import { UtmStorageService } from '@core/services/utm-storage/utm-storage.service';

import { NoConnectionModalComponent } from '@shared/modals/no-connection-modal/no-connection-modal.component';
import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
import { BrevoService } from '@core/services/plugin/brevo/brevo.service';
import { PageHistoryService } from '@core/services/navigation-history/page-history.service';
import { Store } from '@ngrx/store';
import { PAGE } from '@store/page/page.action';
import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { VerifiedUserEmailSuccessAlertModalData } from '@shared/modals/verified-user-email-success-alert-modal/verified-user-email-success-alert-modal.component';
import { AUTH_STORE_LOCAL_ACTIONS } from '@store/authentication';
import { FooterStickyModalService } from '@core/services/footer-sticky-modal/footer-sticky-modal.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { PreviewBridgeService } from '@core/services/cb2-preview/preview-bridge.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent extends DestroyerBase implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private cartRefiner = inject(CartStoreRefiner);
  private readonly pwaService = inject(PwaService);
  private cartSelector = inject(CartStoreSelector);
  private shopSelector = inject(ShopStoreSelector);
  private authSelector = inject(AuthStoreSelector);
  private orderSelector = inject(OrderStoreSelector);
  private basketRefiner = inject(BasketStoreRefiner);
  private basketSelector = inject(BasketStoreSelector);
  private cartDispatcher = inject(CartStoreDispatcher);
  private paymentSelector = inject(PaymentStoreSelector);
  private basketDispatcher = inject(BasketStoreDispatcher);
  private settingsSelector = inject(SettingsStoreSelector);
  private settingsDispatcher = inject(SettingsStoreDispatcher);
  private marketplaceSelector = inject(MarketplaceStoreSelector);
  private onSiteLocationSelector = inject(OnSiteLocationStoreSelector);
  private onSiteLocationDispatcher = inject(OnSiteLocationStoreDispatcher);
  private readonly cartService = inject(CartService);
  private readonly comoService = inject(ComoService);
  private readonly kioskService = inject(KioskService);
  private readonly updateService = inject(UpdateService);
  private readonly ordersService = inject(OrdersService);
  private readonly modalsService = inject(ModalsService);
  private readonly basketService = inject(BasketService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly canonicalService = inject(CanonicalService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly marketplaceService = inject(MarketplaceService);
  private readonly authFirebaseService = inject(AuthFirebaseService);
  private readonly localizeRouterService = inject(LocalizeRouterService);
  private readonly navigationHistoryService = inject(NavigationHistoryService);
  private readonly noConnectionModalService = inject(NoConnectionModalService);
  private readonly shopSearchParametersService = inject(ShopSearchParametersService);
  private readonly hiddenCommentStorageService = inject(HiddenCommentStorageService);
  private readonly utmStorageService = inject(UtmStorageService);
  private readonly doodAppLoaderService = inject(DoodAppLoaderService);
  private readonly brevoTracking = inject(BrevoService);
  private readonly pageHistoryService = inject(PageHistoryService);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly footerStickyModalService = inject(FooterStickyModalService);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBridge = inject(PreviewBridgeService);

  layout$ = this.marketplaceService.getLayout$();
  displayNoConnectionMessage$ = this.noConnectionModalService.getDisplay$();
  footerStickyModalHeight$ = this.footerStickyModalService.height$;

  constructor() {
    super();
    locale('fr');
    const isCbPreview = this.detectCbPreviewMode();
    if (this.redirectToAllowedMarketplaceLocale()) {
      return;
    }
    if (!isCbPreview) {
      this.ensureToBeOnTheMainMarketplaceDomain();
    }
    this.checkAndInitLanguage();
    this.handleEmailVerifiedParam();
    this.initDayjsLocale();
    this.translateInitialRoute();
    this.navigationHistoryService.pushState('home', '/');
    this.canonicalService.createCanonicalLink();
    this.authFirebaseService.initAuthTokenRefresh();
    this.brevoTracking.initializeTracker();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._destroyerRef),
      )
      .subscribe(event => {
        const urlAfterRedirects = (event as NavigationEnd).urlAfterRedirects;
        this.pageHistoryService.addPage(urlAfterRedirects);
        this.captureUtmFromUrl(urlAfterRedirects);
        this.captureHiddenCommentFromUrl(urlAfterRedirects);
        this.applyDistributionModeQueryParamsFromCurrentUrl();
      });
  }

  ngAfterViewInit(): void {
    this.doodAppLoaderService.hide();
    this.store.dispatch(PAGE.appComponentInitialized());
  }

  ngOnInit(): void {
    this.analyticsService.trackEvent('webbase_initialized');
    if (!this.cb2PreviewState.isActive()) {
      this.updateService.checkForUpdate();
      this.pwaService.initialize();
    } else {
      // Arm the postMessage bridge with the dashboard parent window.
      this.previewBridge.init();
    }
    this.updateDeviceScreenType();
    this.synchronizeOrderStatusAfterLostConnection();
    this.cartDispatcher.checkLastCartUpdate();
    this.storeOnSiteLocationIdFromQueryParams();
    this.applyDistributionModeQueryParamsFromCurrentUrl();
    this.storeDeliverySearchParametersFromQueryParams();
    this.storeHiddenCommentFromQueryParams();
    this.storeUtmFromQueryParams();
    this.kioskService.initializeKiosk();
    this.authFirebaseService.startAnonymousUserLogoutTimer();
    this.handleSignInWithCustomToken();
    this.comoService.handleSingleSignOn();

    const isSignInWithRedirect = this.authSelector.selectStatus().redirect;

    if (isSignInWithRedirect) {
      this.modalsService.open(AuthenticationModalComponent.handle);
    }

    this.displayNoConnectionMessage$
      .pipe(takeUntil(this._destroyerRef), distinctUntilChanged())
      .subscribe(display => {
        if (display) {
          this.modalsService.open(NoConnectionModalComponent.handle);
        } else {
          this.modalsService.close(NoConnectionModalComponent.handle);
        }
      });
  }

  handleSignInWithCustomToken(): void {
    const urlAuthTokenSearchParams = new URLSearchParams(window.location.search);
    if (!urlAuthTokenSearchParams.has('authToken')) {
      return;
    }

    const authToken = urlAuthTokenSearchParams.get('authToken');

    if (!authToken) {
      return;
    }

    console.log('[App] Connecting to with authToken "' + authToken + '"');

    this.authFirebaseService
      .signInWithCustomToken(authToken)
      .pipe(
        take(1),
        tap(result => console.log('[App] Result', result)),
        // tap(() => document.location.reload())
        // @fixme
      )
      .subscribe();
  }

  private applyDistributionModeQueryParamsFromCurrentUrl(): void {
    this.storeForceDistributionModeFromQueryParams();
    this.storeSetDistributionModeFromQueryParams();
  }

  private storeForceDistributionModeFromQueryParams(): void {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has('forceDistributionMode')) {
      const forceDistributionMode = urlSearchParams.get('forceDistributionMode') as string;
      this.forceDistributionMode(forceDistributionMode);
    }
  }

  private storeSetDistributionModeFromQueryParams(): void {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has('setDistributionMode')) {
      const setDistributionMode = urlSearchParams.get('setDistributionMode') as string;
      if (!urlSearchParams.has('forceDistributionMode')) {
        this.shopSearchParametersService.setForceDistributionMode(null);
      }
      this.setDistributionMode(setDistributionMode);
    }
  }

  private storeDeliverySearchParametersFromQueryParams(): void {
    const paramsToCheck = [
      'deliveryStreetAddress',
      'deliveryPostalCode',
      'deliveryCity',
      'deliveryLat',
      'deliveryLng',
    ];
    const urlSearchParams = new URLSearchParams(window.location.search);

    const allParamsPresent = paramsToCheck.every(param => urlSearchParams.has(param));
    if (allParamsPresent) {
      const allParams: DoodLocationParameters = {
        deliveryStreetAddress: urlSearchParams.get('deliveryStreetAddress') as string,
        deliveryPostalCode: urlSearchParams.get('deliveryPostalCode') as string,
        deliveryCity: urlSearchParams.get('deliveryCity') as string,
        deliveryLat: Number(urlSearchParams.get('deliveryLat')),
        deliveryLng: Number(urlSearchParams.get('deliveryLng')),
        deliveryCountry: (urlSearchParams.get('deliveryCountry') as string) ?? 'FR',
        deliveryInstructions: urlSearchParams.get('deliveryInstructions') as string,
      };

      this.shopSearchParametersService.setLocationFromQueryParams(allParams);

      const cart = this.cartSelector.active;
      if (cart) {
        let newCart = {
          delivery_address: {
            point: {
              coordinates: [
                Number(urlSearchParams.get('deliveryLng')),
                Number(urlSearchParams.get('deliveryLat')),
              ],
            },
            city: urlSearchParams.get('deliveryCity') ?? '',
            country: urlSearchParams.get('deliveryCountry') ?? '',
            post_code: urlSearchParams.get('deliveryPostalCode'),
            street_line_1: urlSearchParams.get('deliveryStreetAddress') ?? '',
            instructions: urlSearchParams.get('deliveryInstructions') ?? null,
          },
        };
        this.cartDispatcher.updateActive(newCart);
      }
    }
  }

  private storeHiddenCommentFromQueryParams(): void {
    this.hiddenCommentStorageService.captureFromQueryParams(
      new URLSearchParams(window.location.search),
    );
  }

  private captureHiddenCommentFromUrl(url: string): void {
    const queryIndex = url.indexOf('?');
    const search = queryIndex >= 0 ? url.slice(queryIndex + 1) : '';
    this.hiddenCommentStorageService.captureFromQueryParams(new URLSearchParams(search));
  }

  private storeUtmFromQueryParams(): void {
    this.utmStorageService.captureFromQueryParams(new URLSearchParams(window.location.search));
  }

  private captureUtmFromUrl(url: string): void {
    const queryIndex = url.indexOf('?');
    const search = queryIndex >= 0 ? url.slice(queryIndex + 1) : '';
    this.utmStorageService.captureFromQueryParams(new URLSearchParams(search));
  }

  private storeOnSiteLocationIdFromQueryParams(): void {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has('onSiteLocationId')) {
      const onSiteLocationId = urlSearchParams.get('onSiteLocationId') as string;

      // Check if the onSiteLocationId is invalid
      if (onSiteLocationId === 'INVALID_ON_SITE_LOCATION') {
        // Don't store the invalid location ID, just show an error modal
        this.modalsService.open(AlertModalComponent.handle);
        this.modalsService.setData(AlertModalComponent.handle, {
          title: this.translateService.instant('errors.on-site-location-invalid'),
          message: this.translateService.instant('errors.on-site-location-invalid'),
        });
        return;
      }

      const orderType = OrderTypeValues.OnSite;

      this.onSiteLocationDispatcher.updateSettings({
        orderType,
        id: onSiteLocationId,
        lastLocationId: onSiteLocationId,
        source: DoodOnSiteLocationSourceValues.fromQrCode,
      });

      this.shopSearchParametersService.setDistributionModeType(orderType, true);
      this.forceDistributionMode(orderType);
      this.shopSearchParametersService.setDefinedDistributionModeType(true);
    }

    combineLatest([
      this.onSiteLocationSelector.selectSettings.pipe(
        distinctUntilChanged((p, c) => p?.id === c?.id),
      ),
      this.cartRefiner.selectHasCart,
      this.basketRefiner.selectHasBasket,
      this.shopSelector.selectActive$,
      this.cartSelector.selectActive$.pipe(
        distinctUntilChanged((p, c) => {
          return p?.type === c?.type && p?.on_site_location_id === c?.on_site_location_id;
        }),
      ),
      this.onSiteLocationSelector.selectLocations,
    ])
      .pipe(
        tap(([onSiteLocation, cartExist, basketExist, activeShop, activeCart, onSiteLocations]) => {
          if (!cartExist && !onSiteLocation.id) {
            return;
          }

          const onsiteLocationPickMode = activeShop?.[ShopKeys.DistributionModes].filter(
            distributionMode => {
              return distributionMode[DistributionModeKeys.Type] === DistributionModeValues.OnSite;
            },
          )?.[0]?.[DistributionModeKeys.OnsiteLocationPickMode];

          const { id: onSiteLocationId, source } = this.onSiteLocationSelector.settings;
          const isSourceFromUrl = source === DoodOnSiteLocationSourceValues.fromQrCode;

          const basketOnSiteLocationId = this.basketSelector.basket.on_site_location_id;

          const onSiteLocationName = onSiteLocations?.filter(
            (location: DoodOnSiteLocationModel) =>
              location[OnSiteLocationKeys.Id] === (basketOnSiteLocationId ?? onSiteLocation.id),
          )?.[0]?.[OnSiteLocationKeys.Name];

          const isJointCartQueryParam =
            !!this.activatedRoute.snapshot.queryParams?.[UrlParamsKeys.JoinCartId] &&
            !!this.activatedRoute.snapshot.queryParams?.[UrlParamsKeys.ShareCode];

          if (
            basketOnSiteLocationId &&
            onSiteLocations.find(
              (location: DoodOnSiteLocationModel) =>
                location[OnSiteLocationKeys.Id] === basketOnSiteLocationId,
            )
          ) {
            this.onSiteLocationDispatcher.updateSettings({
              id: basketOnSiteLocationId,
              source: DoodOnSiteLocationSourceValues.fromUser,
            });
          }

          if (
            !onsiteLocationPickMode ||
            onsiteLocationPickMode === OnSiteLocationPickModeValues.Url ||
            (onsiteLocationPickMode === OnSiteLocationPickModeValues.UrlOrDropdown &&
              isSourceFromUrl &&
              onSiteLocationId)
          ) {
            if (activeCart && onSiteLocation.id) {
              this.cartDispatcher.updateActive({
                [CartKeys.OnSiteLocationName]: onSiteLocationName,
                [CartKeys.OnSiteLocationId]: basketOnSiteLocationId ?? onSiteLocation.id,
              });
            }
            return;
          }

          if (
            onsiteLocationPickMode === OnSiteLocationPickModeValues.UrlOrDropdown &&
            isSourceFromUrl &&
            onSiteLocationId
          ) {
            return;
          }

          const marketplace = this.marketplaceSelector.marketplace;

          const isCartScopShop =
            marketplace?.[MarketplaceKeys.CartScope] === MarketplaceCartScopeValues.Shop;
          const isOnSiteCart = activeCart?.[CartKeys.Type] === DistributionModeValues.OnSite;
          const isLocationIdMismatch =
            activeCart?.[CartKeys.OnSiteLocationId] !== onSiteLocation.id;
          const isLocationIdMissing = !onSiteLocation.id;

          if (
            onSiteLocation.source === DoodOnSiteLocationSourceValues.fromUser &&
            isCartScopShop &&
            activeShop &&
            !basketExist &&
            !isJointCartQueryParam
          ) {
            const shouldOpenOnSiteLocationPicker =
              ((isOnSiteCart && isLocationIdMismatch) || (isOnSiteCart && isLocationIdMissing)) &&
              (onSiteLocations?.length ?? 0) > 1;

            if (shouldOpenOnSiteLocationPicker) {
              this.modalsService.open(CartParametersModalComponent.handle);
            } else if (
              isOnSiteCart &&
              (isLocationIdMismatch || isLocationIdMissing) &&
              onSiteLocations?.[0]?.id
            ) {
              this.cartDispatcher.updateActive({
                [CartKeys.OnSiteLocationName]: onSiteLocations[0].name,
                [CartKeys.OnSiteLocationId]: onSiteLocations[0].id,
              });
            }
          }

          if (onSiteLocation.id) {
            this.cartDispatcher.updateActive({
              [CartKeys.OnSiteLocationName]: onSiteLocationName,
              [CartKeys.OnSiteLocationId]: basketOnSiteLocationId ?? onSiteLocation.id,
            });

            if (basketExist) {
              this.basketDispatcher.updateBasket({
                on_site_location_id: basketOnSiteLocationId ?? onSiteLocation.id,
              });
              this.basketService.updateBasket$().pipe(take(1)).subscribe();
            }
          }
        }),
      )
      .subscribe();
  }

  private updateDeviceScreenType(): void {
    fromEvent(window, 'resize')
      .pipe(
        takeUntil(this._destroyerRef),
        debounceTime(1000),
        map(() => window.innerWidth), // Don't use mapTo!
        distinctUntilChanged(),
        startWith(window.innerWidth),
      )
      .subscribe(() => {
        this.settingsDispatcher.calculateDevice();
      });
  }

  private handleEmailVerifiedParam(): void {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.has('email-verified') && urlSearchParams.get('email-verified') === 'true') {
      const user = this.authSelector.selectUser();
      if (user) {
        this.store.dispatch(
          AUTH_STORE_LOCAL_ACTIONS.updateUser({
            user: {
              ...user,
              email_verified: true,
            },
          }),
        );
      }
      // Remove the email-verified parameter from URL
      urlSearchParams.delete('email-verified');

      // Update the URL without the parameter
      const newSearch = urlSearchParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}${window.location.hash}`;

      // Replace the current URL without adding to history
      window.history.replaceState(null, '', newUrl);

      this.modalsService.open<VerifiedUserEmailSuccessAlertModalData>(
        'verified-user-email-success-modal',
      );
    }
  }

  /**
   * Hard-navigates when the URL locale is not published by the marketplace.
   * `location.replace` drops the rejected URL from history, and returning before
   * `translateInitialRoute` stops the router from navigating back onto it.
   */
  private redirectToAllowedMarketplaceLocale(): boolean {
    const nextUrl = marketplaceLocaleRedirectUrl(
      window.location.href,
      this.marketplaceLocaleContext(),
    );
    if (!nextUrl) {
      return false;
    }
    window.location.replace(nextUrl);
    return true;
  }

  private marketplaceLocaleContext() {
    return buildMarketplaceLocaleContext(this.marketplaceSelector.marketplace, {
      currentStoreLocale: this.settingsSelector.app.locale,
      browserLang: navigator.language.split('-')[0],
    });
  }

  private checkAndInitLanguage(): void {
    const decision = decideMarketplaceLocale(
      window.location.pathname,
      this.marketplaceLocaleContext(),
    );
    if (decision.action !== 'keep') {
      return;
    }

    if (decision.locale !== this.settingsSelector.app.locale) {
      this.settingsDispatcher.updateAppLocale(decision.locale);
    }

    this.localizeRouterService.changeLanguage(decision.locale, {
      replaceUrl: true,
    });
  }

  private detectCbPreviewMode(): boolean {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const isActive = urlSearchParams.has(UrlParamsKeys.CbPreview);
    if (isActive) {
      this.cb2PreviewState.activate();
    }
    return isActive;
  }

  private ensureToBeOnTheMainMarketplaceDomain(): void {
    const marketplace = this.marketplaceSelector.marketplace;
    const mainDomain = marketplace?.domains?.[0];
    if (!mainDomain) {
      return;
    }

    const currentUrl = new URL(window.location.href);
    const currentDomain = currentUrl.hostname;

    // Local + PR preview hosts keep ?marketplace_domain= without bouncing
    // to the marketplace canonical domain.
    if (currentDomain !== mainDomain && !isMarketplaceDomainOverrideHost(currentDomain)) {
      location.href =
        'https://' + mainDomain + currentUrl.pathname + currentUrl.search + currentUrl.hash;
    }
  }

  private initDayjsLocale(): void {
    const appLocale = this.settingsSelector.app.locale;
    locale(appLocale);
    document.documentElement.lang = appLocale;
  }

  private translateInitialRoute(): void {
    const queryParamsString = location.search.substring(1);
    const queryParams = this.convertQueryParamsStringToObject(queryParamsString);
    this.router.navigate([this.routerHelper.translateRoute(`${window.location.pathname}`)], {
      queryParams,
    });
  }

  private convertQueryParamsStringToObject(queryParamsString: string): object {
    const params: Record<string, string> = {};
    if (queryParamsString) {
      const searchParams = new URLSearchParams(
        queryParamsString.startsWith('?') ? queryParamsString : '?' + queryParamsString,
      );
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }
    }
    return params;
  }

  private synchronizeOrderStatusAfterLostConnection(): void {
    this.paymentSelector.selectStatus
      .pipe(
        switchMap(status => {
          if (!status.isRetryInProgress) return of(null);
          return this.orderSelector.selectActiveId;
        }),
        switchMap(id => (id ? this.ordersService.loadOrderById$(id.toString()) : of(null))),
        tap(order => {
          if (
            order?.status &&
            [DoodOrderStatus.waiting, DoodOrderStatus.preparation].includes(order?.status)
          ) {
            this.ordersService.deActiveOrderToStore();
            this.ordersService.removeOrderFromStore(order.id);
            this.cartService.clearCart();
            this.paymentDispatcher.resetUI();
            this.router.navigate([
              this.routerHelper.translateRoute(`${Paths.Orders}/${order.id}/${Paths.Status}`),
            ]);
          }
        }),
        catchError(error => {
          this.paymentDispatcher.updateStatus({ isRetryInProgress: true });
          return error;
        }),
        take(1),
        takeUntil(this._destroyerRef),
      )
      .subscribe();
  }

  private forceDistributionMode(distributionMode: string): void {
    this.shopSearchParametersService.setForceDistributionMode(distributionMode);
    this.setDistributionMode(distributionMode);
  }

  // TODO: This method is 98% similar to  the previous one, clean the code...
  private setDistributionMode(distributionMode: string): void {
    this.shopSearchParametersService.setDistributionModeType(distributionMode, true);
  }
}
